import {
  CheckCircledIcon,
  InfoCircledIcon,
  PlusIcon,
  ExclamationTriangleIcon,
} from "@radix-ui/react-icons";
import { setUser } from "@sentry/react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useRouterState,
  Outlet,
} from "@tanstack/react-router";
import { Suspense, useEffect, useMemo, useState } from "react";

import {
  CommunityGuidelinesDialog,
  needsToAgreeToCommunityGuidelines,
  needsToAgreeToPaidService,
  needsToAgreeToPrivacy,
  needsToAgreeToTerms,
  PaymentAgreementDialog,
  PrivacyPolicyDialog,
  TermsAndConditionsDialog,
} from "@/components/AgreementDialogs";
import { CollaboratorNotifications } from "@/components/CollaboratorNotifications";
import { EditEmailDialog } from "@/components/EditEmailDialog";
import { ErrorPage } from "@/components/ErrorPage";
import { useToast } from "@/components/hooks/use-toast";
import { SidebarHelp } from "@/components/SidebarHelp";
import { Spinner } from "@/components/Spinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { ToastAction } from "@/components/ui/toast";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  CampaignNotification,
  campaignNotificationsQueryOptions,
  getAccountDetailsQueryOptions,
  getCurrentUserQueryOptions,
  checkAuth,
  myFeedsQueryOptions,
  getUserDeprecationCheckQueryOptions,
  MessageContext,
} from "@/lib/api";
import { FeatureFlagContext } from "@/lib/feature-flags";
import { useStoredFlag } from "@/lib/hooks";
import { cn } from "@/lib/utils";

function NotFoundComponent() {
  return <ErrorPage title="404" description="Page not found" />;
}

/** Remembers, per browser, that this reader asked for the whole list. */
const SIDEBAR_EXPANDED_KEY = "sidebar-feeds-expanded";

/**
 * How many feeds the default sidebar setting lists before offering to expand.
 *
 * 50, not 10. Of 3,123 owners with feeds, 95 have more than 10 — and 48 of
 * those have between 11 and 20, where a cap does no work at all beyond hiding
 * one or two feeds from them. At 50 only 13 owners ever see the expand row, and
 * the cap still does its real job of not rendering a 421-entry sidebar.
 */
const DEFAULT_SIDEBAR_FEEDS = 50;
/** The most /my_feeds will return in one page, and so the most the sidebar can list. */
const ALL_SIDEBAR_FEEDS = 100;

/**
 * True when the sidebar is showing the most recent feeds rather than filtering
 * by tag.
 *
 * "last-10" is a STORED VALUE, sitting in the sidebar_feeds user setting for
 * everyone who has ever touched this control. It is an opaque key, not a count
 * — the count is DEFAULT_SIDEBAR_FEEDS — and it must not be renamed: anything
 * unrecognised here falls through to the tag branch and gets parseInt'd into
 * NaN, which asks the API for a tag that does not exist. Any future `last-*`
 * spelling is accepted for the same reason.
 */
function isDefaultSetting(
  sidebarFeeds: string | number | undefined,
): sidebarFeeds is undefined | `last-${string}` {
  return (
    !sidebarFeeds ||
    (typeof sidebarFeeds === "string" && sidebarFeeds.startsWith("last-"))
  );
}

function sidebarFeedsQueryOptions(
  sidebarFeeds: string | number | undefined,
  showAll = false,
) {
  if (isDefaultSetting(sidebarFeeds)) {
    return myFeedsQueryOptions({
      page: 1,
      page_size: showAll ? ALL_SIDEBAR_FEEDS : DEFAULT_SIDEBAR_FEEDS,
      // Feeds shared with you belong in the recent list -- otherwise a collaborator has no
      // route to the feed from anywhere but the dashboard. The tag-filtered setting below
      // stays owner-scoped: tags are the owner's, so a shared feed could never match one.
      access: "all",
    });
  }

  // A tag filter means "all my feeds with this tag", so it never asks for ten.
  return myFeedsQueryOptions({
    page: 1,
    page_size: ALL_SIDEBAR_FEEDS,
    tags: [
      typeof sidebarFeeds === "number" ? sidebarFeeds : parseInt(sidebarFeeds),
    ],
  });
}

/**
 * How many feeds somebody else has shared with you.
 *
 * 🔑 THIS EXISTS SO "SHARED WITH YOU" CAN BE A PLACE, NOT JUST A FILTER. Shared feeds are
 * blended into the feed list by default, which is right -- they are your feeds -- but it
 * leaves someone who was just added with nothing in the product naming what happened or
 * where to look. A nav entry needs a count to decide whether to render at all.
 *
 * `page_size: 1` because only `total_items` is read; the row itself is thrown away.
 */
function sharedFeedCountQueryOptions() {
  return myFeedsQueryOptions({ page: 1, page_size: 1, access: "shared" });
}

export const Route = createFileRoute("/app/_sidebar-layout")({
  component: LayoutComponent,
  beforeLoad: checkAuth,
  notFoundComponent: NotFoundComponent,
  loader: async ({ context: { queryClient } }) => {
    const user = await queryClient.ensureQueryData(
      getCurrentUserQueryOptions(),
    );

    return Promise.all([
      queryClient.ensureQueryData(
        sidebarFeedsQueryOptions(user?.settings?.sidebar_feeds),
      ),
      // prefetch, not ensure: an empty sidebar entry is a far better failure than a
      // layout that will not render because a count could not be fetched.
      queryClient.prefetchQuery(sharedFeedCountQueryOptions()),
      queryClient.ensureQueryData(getCurrentUserQueryOptions()),
    ]);
  },
});

function FeedsGroup() {
  const router = useRouterState();
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  // Expanding re-fetches with a bigger page rather than navigating away: the row
  // says "show N more", so it has to actually show them in this list. Remembered
  // in this browser, so someone who wants the full list does not have to ask for
  // it again after every reload.
  const [showAll, setShowAll] = useStoredFlag(SIDEBAR_EXPANDED_KEY);
  const { data: feeds } = useQuery(
    sidebarFeedsQueryOptions(user.settings?.sidebar_feeds, showAll),
  );
  const { data: shared } = useQuery(sharedFeedCountQueryOptions());
  const sharedCount = shared?.total_items ?? 0;
  const notShownCount = feeds
    ? feeds.total_items - feeds.user_algos.length
    : 0;
  // Expanding only adds anything under the "last N" setting; a tag filter already
  // asks for everything, so past the page ceiling the only honest answer is the
  // Feeds page.
  const canExpand =
    !showAll && notShownCount > 0 && isDefaultSetting(user.settings?.sidebar_feeds);
  const canCollapse =
    showAll && (feeds?.user_algos.length ?? 0) > DEFAULT_SIDEBAR_FEEDS;

  return (
    <SidebarGroupContent>
      <SidebarMenu>
        <SidebarMenuItem className="flex items-center gap-2">
          <SidebarMenuButton
            asChild
            isActive={router.location.pathname === "/app/"}
          >
            <Link to="/app" className="flex items-center gap-2">
              Feeds
            </Link>
          </SidebarMenuButton>

          {/* On Feeds rather than on "Shared with you", which is conditional on having a
              live grant -- and so disappears at exactly the moment a removal needs
              reporting. */}
          <CollaboratorNotifications />

          <SimpleTooltip message="Create a new feed">
            <SidebarGroupAction asChild>
              <Link to="/app/create/choice">
                <PlusIcon /> <span className="sr-only">Create a new feed</span>
              </Link>
            </SidebarGroupAction>
          </SimpleTooltip>
        </SidebarMenuItem>

        {/* Only when there is something to see. For the overwhelming majority of people
            nobody has shared a feed with them, and a permanently empty nav entry teaches
            them to ignore that part of the sidebar. */}
        {sharedCount > 0 && (
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link
                to="/app"
                search={{ access: "shared" }}
                className="flex items-center justify-between gap-2"
              >
                <span>Shared with you</span>
                <span className="rounded-md bg-slate-3 dark:bg-slatedark-4 px-1.5 text-xs text-slate-dim">
                  {sharedCount}
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
        {feeds?.user_algos.map((feed) => (
          <SidebarMenuItem key={feed.id}>
            <SidebarMenuButton
              asChild
              isActive={
                router.location.pathname === `/app/feed/${feed.id}/view`
              }
            >
              <Link
                to="/app/feed/$feedId/view"
                params={{ feedId: `${feed.id}` }}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate">
                  {feed.display_name || "Untitled Feed"}
                </span>
                {/* Why this feed is in a list headed by your own: somebody gave it to you. */}
                {feed.shared_with_me && (
                  <span className="shrink-0 text-xs text-slate-dim">Shared</span>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
        {canExpand && (
          <SidebarMenuItem>
            <SidebarMenuButton
              className="text-slate-dim"
              onClick={() => setShowAll(true)}
            >
              Show {notShownCount} more
              {notShownCount === 1 ? " feed" : " feeds"}
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
        {!canExpand && notShownCount > 0 && (
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link to="/app" className="text-slate-dim">
                See all {feeds?.total_items} feeds
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
        {canCollapse && (
          <SidebarMenuItem>
            <SidebarMenuButton
              className="text-slate-dim"
              onClick={() => setShowAll(false)}
            >
              Show fewer
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarMenu>
    </SidebarGroupContent>
  );
}

function CampaignNotificationItem({
  notification,
  onClick,
}: {
  notification: CampaignNotification;
  onClick: () => void;
}) {
  return (
    <Link
      className="flex items-start gap-2 bg-slate-ghost p-2 rounded-md"
      to={notification.link.replace("https://www.graze.social/app", "")}
      onClick={onClick}
    >
      <div className="text-xs font-semibold text-slate-dim mt-px">
        {notification.type === "info" && <InfoCircledIcon />}
        {notification.type === "success" && <CheckCircledIcon />}
        {notification.type === "warning" && <ExclamationTriangleIcon />}
      </div>
      <div className="text-xs font-semibold text-slate-dim">
        {notification.text}
      </div>
    </Link>
  );
}

function CampaignMessageNotificationItem({
  notification,
  onClick,
}: {
  notification: CampaignNotification;
  onClick: () => void;
}) {
  const url = new URL(notification.link);
  const context = JSON.parse(
    url.searchParams.get("context") ?? "{}",
  ) as MessageContext;

  return (
    <Link
      className="flex items-start gap-2 bg-slate-ghost p-2 rounded-md"
      to="/app/campaigns/$campaignId/feed/$algoId/messages"
      onClick={onClick}
      params={{
        campaignId: context.campaign_id.toString(),
        algoId: context.algo_id.toString(),
      }}
    >
      <div className="text-xs font-semibold text-slate-dim mt-px">
        {notification.type === "info" && <InfoCircledIcon />}
        {notification.type === "success" && <CheckCircledIcon />}
        {notification.type === "warning" && <ExclamationTriangleIcon />}
      </div>
      <div className="text-xs font-semibold text-slate-dim">
        {notification.text}
      </div>
    </Link>
  );
}

function CampaignItem() {
  const router = useRouterState();
  const [open, setOpen] = useState(false);
  const { data: notifications } = useQuery(campaignNotificationsQueryOptions());

  return (
    <SidebarMenuItem className="flex items-center gap-1">
      <SidebarMenuButton
        asChild
        isActive={router.location.pathname.includes("/campaigns")}
        className="pr-1"
      >
        <Link
          to="/app/campaigns"
          className="flex items-center gap-2 justify-between"
        >
          Campaigns
        </Link>
      </SidebarMenuButton>
      {notifications && notifications?.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            tabIndex={0}
            onMouseEnter={() => setOpen(true)}
            aria-label="Open campaign notifications"
          >
            <div className="px-2 py-1 text-xs bg-red-11 dark:bg-reddark-9 text-slate-1 dark:text-slatedark-1 rounded-md">
              {notifications.length}
            </div>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            alignOffset={-32}
            className="flex flex-col p-1"
            onMouseLeave={() => setOpen(false)}
          >
            <div className="flex flex-col gap-3">
              {notifications?.map((n) =>
                n.category === "incoming_proposed_campaign" ||
                n.category === "outgoing_accepted_campaign" ? (
                  <CampaignNotificationItem
                    key={n.text}
                    notification={n}
                    onClick={() => setOpen(false)}
                  />
                ) : n.category === "unread_message_thread" ? (
                  <CampaignMessageNotificationItem
                    key={n.text}
                    notification={n}
                    onClick={() => setOpen(false)}
                  />
                ) : null,
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </SidebarMenuItem>
  );
}

function LayoutComponent() {
  const { toast } = useToast();
  const router = useRouterState();
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());

  const { data: accountDetails } = useQuery(getAccountDetailsQueryOptions());
  const featureFlags = useMemo(
    () => user.feature_flags ?? [],
    [user.feature_flags],
  );

  useEffect(() => {
    if (user.account?.email) return;

    requestAnimationFrame(() => {
      const { dismiss } = toast({
        title: "Please add your email to your account",
        description: "This is required to use the app",
        action: <EditEmailDialog onClose={() => dismiss()}></EditEmailDialog>,
        duration: 9999999,
      });
    });
  }, [user.account?.email, toast]);

  const { data: deprecationCheck } = useQuery(
    getUserDeprecationCheckQueryOptions(),
  );
  useEffect(() => {
    if (
      deprecationCheck?.algorithms &&
      Object.keys(deprecationCheck.algorithms).length > 0
    ) {
      toast({
        duration: 9999999,
        title: "You have feeds using deprecated features",
        description:
          "These features will be sunset soon and you must update them!",
        action: (
          <ToastAction asChild altText="Update your Feeds">
            <Link to="/app/account" hash="deprecation-check">
              View
            </Link>
          </ToastAction>
        ),
      });
    }
  }, [deprecationCheck, toast]);

  useEffect(() => {
    const crisp = (
      window as unknown as { $crisp: { push: (args: unknown[]) => void } }
    ).$crisp;

    if (user.account?.email) {
      crisp.push(["set", "user:email", [user.account.email]]);
    }

    crisp.push(["set", "user:nickname", [user.username]]);

    return () => {
      crisp.push(["do", "session:reset"]);
    };
  }, [user]);

  useEffect(() => {
    setUser({
      email: user.account?.email,
      username: user.username,
      id: user.id,
    });
  }, [user]);

  return (
    <SidebarProvider>
      <FeatureFlagContext value={featureFlags}>
        <div className="flex-1 min-h-0 grid md:max-w-[calc(1216px+280px)] md:grid-cols-[280px_1fr] xl:max-w-[calc(1216px+280px+280px)] xl:grid-cols-[280px_1fr] mx-auto w-screen">
          <Sidebar>
            <SidebarHeader className="text-xl font-semibold">
              <Link to="/" className="flex items-center gap-4">
                <span
                  className={cn(
                    "cursor-pointer ",
                    "sharp-grotesk text-2xl md:text-5xl",
                    "text-slate-normal",
                  )}
                >
                  Graze
                </span>
              </Link>
            </SidebarHeader>
            <SidebarContent>
              <SidebarGroup>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={router.location.pathname === "/app/account"}
                    >
                      <Link
                        to="/app/account"
                        className="flex items-center gap-2"
                      >
                        Account
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>
              <SidebarGroup>
                <FeedsGroup />
              </SidebarGroup>
              <SidebarGroup>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={router.location.pathname.includes(
                        "/subscriber-lists",
                      )}
                    >
                      <Link
                        to="/app/subscriber-lists"
                        className="flex items-center gap-2"
                      >
                        Subscriber Lists
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={router.location.pathname.includes(
                        "/sticky-sets",
                      )}
                    >
                      <Link
                        to="/app/sticky-sets"
                        className="flex items-center gap-2"
                      >
                        Sticky Sets
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <CampaignItem />
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={router.location.pathname.includes(
                        "/custom-nodes",
                      )}
                    >
                      <Link
                        to="/app/custom-nodes"
                        className="flex items-center gap-2"
                      >
                        Custom Nodes
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>
            </SidebarContent>
            <SidebarFooter>
              <SidebarHelp>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/docs">Help</Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarHelp>
            </SidebarFooter>
          </Sidebar>
          <main className="flex-1 min-w-0 bg-slate-app">
            <SidebarTrigger />

            <Suspense
              fallback={
                <div className="h-screen w-full flex items-center justify-center">
                  <Spinner delay={500} />
                </div>
              }
            >
              <Outlet />
            </Suspense>

            {needsToAgreeToPrivacy(user) ? (
              <PrivacyPolicyDialog defaultOpen />
            ) : needsToAgreeToTerms(user) ? (
              <TermsAndConditionsDialog defaultOpen />
            ) : needsToAgreeToPaidService(user, accountDetails) ? (
              <PaymentAgreementDialog defaultOpen />
            ) : needsToAgreeToCommunityGuidelines(user) ? (
              <CommunityGuidelinesDialog defaultOpen />
            ) : null}
          </main>
        </div>
      </FeatureFlagContext>
    </SidebarProvider>
  );
}
